/**
 * LanguageSwitcher — the RU / KG / EN control (ADR-028: all three first-class).
 *
 * WHAT IT REPLACES. Three full-size buttons rendered side by side, spelling
 * out "Русский" "Кыргызча" "English" in the app header at every desktop width.
 * Measured on the shipped 1440px header that cluster was ~330px — roughly a
 * quarter of the entire bar — spent restating two options the user has already
 * declined, and it was the single loudest reason the header read as
 * unbalanced. It also could not be shown on mobile at all (the shell hid it
 * below `md`), so the language control was missing exactly where this product
 * is used: one-handed, in a bazaar.
 *
 * WHAT IT IS NOW. A ~60px control that states the ACTIVE language as its
 * two-letter tag (RU / KG / EN) and opens a small menu holding the other two,
 * each written in its own language ("Кыргызча" is Kyrgyz for Kyrgyz — a
 * language picker that names a language in a language you cannot read is a
 * dead end). It fits at 320px, so the switcher is now present at every width.
 *
 * Behaviour is unchanged: selection writes through `ui-store.setLanguage`, the
 * authoritative path that persists the preference AND calls
 * `i18n.changeLanguage`, so strings switch immediately and the choice survives
 * a reload. RU remains the fallback locale.
 *
 * CONTRACT CHANGE (deliberate, 2026-08): the per-language buttons now live
 * inside a menu, so they exist in the DOM only while it is open. The testids
 * are unchanged (`language-switcher`, `language-switcher-{ru,ky,en}`) and a
 * `language-switcher-trigger` was added; call sites that clicked a language
 * directly must open the trigger first. Two tests were updated for this.
 *
 * A11y: `aria-haspopup="menu"` + `aria-expanded` on the trigger, the panel is
 * a `role="menu"` of `role="menuitemradio"` rows carrying `aria-checked`,
 * arrow keys / Home / End move between them, Escape and an outside click
 * dismiss and return focus to the trigger, and the trigger is a 44px target.
 * The active language is conveyed by a check glyph and by `aria-checked`,
 * never by colour alone.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/i18n";
import { menuVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";
import { useUiStore, type UiState } from "@/lib/ui-store";

const selectLanguage = (state: UiState): SupportedLanguage => state.language;
const selectSetLanguage = (state: UiState): UiState["setLanguage"] =>
  state.setLanguage;

export interface LanguageSwitcherProps {
  /** Extra classes for the control root (e.g. `hidden sm:inline-flex`). */
  className?: string;
}

export function LanguageSwitcher({
  className,
}: LanguageSwitcherProps): ReactElement {
  const { t } = useTranslation("common");
  const language = useUiStore(selectLanguage);
  const setLanguage = useUiStore(selectSetLanguage);
  const { reduced, overlay } = useGardenMotion();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const close = useCallback((restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Dismissal: outside pointer, Escape, or focus leaving the control entirely
  // (Tab out) — the same contract the account menu and the search overlay use.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close(true);
      }
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, close]);

  // Opening with the keyboard has to land somewhere useful: focus the language
  // you are currently on, so Down/Up walks away from it in either direction.
  useEffect(() => {
    if (!open) return;
    const activeIndex = Math.max(0, SUPPORTED_LANGUAGES.indexOf(language));
    itemRefs.current[activeIndex]?.focus();
  }, [open, language]);

  const focusItem = useCallback((index: number): void => {
    const count = SUPPORTED_LANGUAGES.length;
    const next = ((index % count) + count) % count;
    itemRefs.current[next]?.focus();
  }, []);

  const onItemKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>, index: number): void => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        focusItem(index + 1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        focusItem(index - 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        focusItem(0);
      } else if (event.key === "End") {
        event.preventDefault();
        focusItem(SUPPORTED_LANGUAGES.length - 1);
      } else if (event.key === "Tab") {
        setOpen(false);
      }
    },
    [focusItem],
  );

  const pick = useCallback(
    (lng: SupportedLanguage): void => {
      setLanguage(lng);
      close(true);
    },
    [setLanguage, close],
  );

  return (
    <div
      ref={rootRef}
      data-testid="language-switcher"
      className={["relative inline-flex shrink-0", className]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("language.current", {
          language: t(`language.${language}`),
        })}
        data-testid="language-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={[
          "inline-flex h-11 items-center gap-1 rounded-[10px] px-2.5",
          "border border-[color:var(--g-rule)] bg-transparent",
          "text-[0.8125rem] font-bold uppercase tracking-[0.06em] text-foreground",
          "transition-colors hover:bg-[color:var(--g-sunken)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          open ? "bg-[color:var(--g-sunken)]" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {t(`language.code.${language}`)}
        <ChevronDown
          {...iconProps("xs")}
          className={[
            "text-[color:var(--g-ink-3)] transition-transform duration-150",
            "motion-reduce:transition-none",
            open ? "rotate-180" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            aria-label={t("language.switchTo")}
            data-testid="language-switcher-menu"
            className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[11rem] overflow-hidden rounded-[12px] border border-[color:var(--g-rule)] bg-popover text-popover-foreground shadow-[var(--g-shadow-lg)]"
            variants={reduced ? undefined : menuVariants}
            {...overlay()}
          >
            <ul className="g-ruled m-0 list-none p-0">
              {SUPPORTED_LANGUAGES.map((lng, index) => {
                const active = lng === language;
                return (
                  <li key={lng}>
                    <button
                      ref={(node) => {
                        itemRefs.current[index] = node;
                      }}
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      data-testid={`language-switcher-${lng}`}
                      onClick={() => pick(lng)}
                      onKeyDown={(event) => onItemKeyDown(event, index)}
                      className={[
                        "flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left text-sm",
                        "transition-colors hover:bg-[color:var(--g-sunken)]",
                        "focus-visible:bg-[color:var(--g-sunken)] focus-visible:outline-none",
                        active
                          ? "font-semibold text-foreground"
                          : "font-medium text-[color:var(--g-ink-2)]",
                      ].join(" ")}
                    >
                      <span className="w-9 shrink-0 text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-[color:var(--g-ink-3)]">
                        {t(`language.code.${lng}`)}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {t(`language.${lng}`)}
                      </span>
                      {active ? (
                        <Check {...iconProps("sm")} className="text-primary" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default LanguageSwitcher;
